import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useOrgStore } from '../../shared/store/org.store.js'
import { getDashboard, type DashboardResponse } from '../../shared/api/orders.js'
import { ApiError } from '../../shared/types/api.js'
import { ROUTES } from '../../app/routes.js'
import OrderStatusBadge from '../orders/components/OrderStatusBadge.js'

type LoadStatus = 'loading' | 'ready' | 'error' | 'forbidden'

function formatTime(value: string, timezone: string): string {
  try {
    return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: timezone })
  } catch {
    return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
}

export default function DashboardPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const activeOrg = useOrgStore(s => s.activeOrganization)
  const orgId = activeOrg?.id

  const [data, setData] = useState<DashboardResponse | null>(null)
  const [status, setStatus] = useState<LoadStatus>('loading')

  const load = useCallback(async () => {
    if (!orgId) return
    setStatus('loading')
    try {
      const result = await getDashboard(orgId)
      setData(result)
      setStatus('ready')
    } catch (err: unknown) {
      setStatus(err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error')
    }
  }, [orgId])

  useEffect(() => {
    void load()
  }, [load])

  if (!orgId || !activeOrg) return null

  return (
    <section className="page-section page-section--wide">
      <div className="page-header">
        <div>
          <h2>{activeOrg.name}</h2>
          <p className="page-section-description">{t('dashboard.subtitle')}</p>
        </div>
        <Link className="btn btn-primary" to={ROUTES.ORDERS}>{t('dashboard.goToOrders')}</Link>
      </div>

      {status === 'loading' && !data && (
        <div className="page-loading"><div className="spinner" /></div>
      )}

      {(status === 'error' || status === 'forbidden') && (
        <div className="settings-card">
          <div className="form-error-banner">
            {status === 'forbidden' ? t('dashboard.forbidden') : t('dashboard.loadError')}
          </div>
          <button type="button" className="btn btn-primary" onClick={() => void load()}>
            {t('error.retry')}
          </button>
        </div>
      )}

      {data && (
        <div className="dashboard-grid">
          <section className="dashboard-card" aria-labelledby="dashboardRecentTitle">
            <div className="dashboard-card-header">
              <h3 id="dashboardRecentTitle">{t('dashboard.recentOrders.title')}</h3>
              <span className="field-hint">{t('dashboard.localDate', { date: data.localDate })}</span>
            </div>

            {data.recentOrders.length === 0 ? (
              <p className="dashboard-empty">{t('dashboard.recentOrders.empty')}</p>
            ) : (
              <ul className="dashboard-order-list">
                {data.recentOrders.map(order => (
                  <li key={order.id}>
                    <button
                      type="button"
                      className="dashboard-order-row"
                      onClick={() => navigate(ROUTES.ORDERS_DETAIL.replace(':orderId', order.id))}
                    >
                      <span className="dashboard-order-main">
                        <span className="order-number">{order.orderNumber}</span>
                        <span className="dashboard-order-customer">{order.guestName || '—'}</span>
                      </span>
                      <OrderStatusBadge status={order.status} />
                      <span className="dashboard-order-total">{order.total} {order.currency}</span>
                      <span className="dashboard-order-time">{formatTime(order.createdAt, data.timezone)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <Link className="dashboard-card-link" to={ROUTES.ORDERS}>{t('dashboard.recentOrders.all')}</Link>
          </section>

          <section className="dashboard-card" aria-labelledby="dashboardPopularTitle">
            <div className="dashboard-card-header">
              <h3 id="dashboardPopularTitle">{t('dashboard.popularDishes.title')}</h3>
            </div>

            {data.popularDishes.length === 0 ? (
              <p className="dashboard-empty">{t('dashboard.popularDishes.empty')}</p>
            ) : (
              <ol className="dashboard-dish-list">
                {data.popularDishes.map((dish, index) => (
                  <li key={`${dish.name}-${index}`} className="dashboard-dish-row">
                    <span className="dashboard-dish-rank">{index + 1}</span>
                    <span className="dashboard-dish-name">{dish.name}</span>
                    <span className="dashboard-dish-quantity">
                      {t('dashboard.popularDishes.quantity', { count: dish.quantity })}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>
      )}
    </section>
  )
}
